// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Link2, ChevronRight, Heart, Cog, Shield, Pencil, Plus, Settings } from 'lucide-react';
import { useProcessChain, useProcessChainsList } from '@/api/queries/useProcessChains';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { ProcessTabsNav } from '@/components/processes/ProcessTabsNav';
import { Skeleton, Button, Page, PageContent } from '@bliks-io/ui';
import { EditProcessesDialog } from './EditProcessesDialog';
import { EditChainDialog } from './EditChainDialog';
import { ChainGridSkeleton } from './ChainGridSkeleton';
import type { PopulatedChainProcess } from '@bliks-io/contracts';
import { processDetailState } from '@/lib/process-utils';

// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated

// ============================================================================
// Breadcrumb
// ============================================================================

function ChainBreadcrumb({
  chain,
  ancestors
}: {
  chain: { _id: string; name: string; parentChainId?: string | null };
  ancestors: { _id: string; name: string }[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useAuthStore().slug();

  return (
    <nav className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
      <button
        className="hover:text-foreground transition-colors"
        onClick={() => navigate({ to: '/$slug/processes/process-map', params: { slug } })}
      >
        {t('processChains.title')}
      </button>
      {ancestors.map((a) => (
        <span key={a._id} className="flex items-center gap-1">
          <ChevronRight className="h-3 w-3" />
          <button
            className="hover:text-foreground transition-colors"
            onClick={() =>
              navigate({
                to: '/$slug/processes/process-chains/$chainId',
                params: { slug, chainId: a._id }
              })
            }
          >
            {a.name}
          </button>
        </span>
      ))}
      <ChevronRight className="h-3 w-3" />
      <span className="text-foreground font-medium">{chain.name}</span>
    </nav>
  );
}

// ============================================================================
// Swimlane Visualization (simple CSS-based, no ReactFlow needed for MVP)
// ============================================================================

const SWIMLANE_CONFIG = {
  strategic: {
    labelKey: 'processChains.edit.swimlane.strategic',
    color: 'bg-teal-50 border-teal-200',
    textColor: 'text-teal-700',
    icon: Shield
  },
  core: {
    labelKey: 'processChains.edit.swimlane.core',
    color: 'bg-blue-50 border-blue-200',
    textColor: 'text-blue-700',
    icon: Heart
  },
  support: {
    labelKey: 'processChains.edit.swimlane.support',
    color: 'bg-orange-50 border-orange-200',
    textColor: 'text-orange-700',
    icon: Cog
  }
} as const;

function SwimlaneView({
  processes,
  onProcessClick
}: {
  processes: PopulatedChainProcess[];
  onProcessClick: (processId: string) => void;
}) {
  const { t } = useTranslation();
  const sorted = useMemo(() => [...processes].sort((a, b) => a.order - b.order), [processes]);

  const maxOrder = sorted.length > 0 ? sorted[sorted.length - 1].order : 0;

  const swimlanes = useMemo(() => {
    const lanes: Record<string, PopulatedChainProcess[]> = {
      strategic: [],
      core: [],
      support: []
    };
    for (const p of sorted) {
      lanes[p.swimlane]?.push(p);
    }
    return lanes;
  }, [sorted]);

  return (
    <div className="border rounded-lg overflow-x-auto">
      {/* Number header */}
      <div className="flex border-b bg-muted/30">
        <div className="w-32 shrink-0 p-2 text-xs font-medium text-muted-foreground border-r" />
        {Array.from({ length: maxOrder }, (_, i) => (
          <div
            key={i + 1}
            className="min-w-[140px] flex-1 p-2 text-center text-xs font-medium text-muted-foreground border-r last:border-r-0"
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Swimlane rows */}
      {(['strategic', 'core', 'support'] as const).map((lane) => {
        const config = SWIMLANE_CONFIG[lane];
        const Icon = config.icon;
        return (
          <div key={lane} className="flex border-b last:border-b-0 min-h-[80px]">
            {/* Lane label */}
            <div className={`w-32 shrink-0 p-2 border-r flex items-center gap-1 ${config.color}`}>
              <Icon className={`h-3 w-3 ${config.textColor}`} />
              <span className={`text-xs font-medium ${config.textColor}`}>
                {t(config.labelKey)}
              </span>
            </div>
            {/* Process cards in this lane */}
            {Array.from({ length: maxOrder }, (_, i) => {
              const proc = swimlanes[lane].find((p) => p.order === i + 1);
              return (
                <div
                  key={i + 1}
                  className="min-w-[140px] flex-1 p-1.5 border-r last:border-r-0 flex items-center justify-center"
                >
                  {proc && (
                    <button
                      onClick={() => onProcessClick(proc.processId)}
                      className={`w-full rounded-md border p-2 text-left transition-colors hover:shadow-xs ${config.color} hover:opacity-80`}
                    >
                      <span className={`text-3xs font-bold ${config.textColor}`}>{proc.order}</span>
                      <p className="text-xs font-medium mt-0.5 line-clamp-3">{proc.processName}</p>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Sub-Chains View (for group chains)
// ============================================================================

function SubChainsView({ parentChainId }: { parentChainId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useAuthStore().slug();
  const { data: children, isLoading } = useProcessChainsList({ parentChainId });

  if (isLoading) return <ChainGridSkeleton />;

  if (!children?.length) {
    return <p className="text-sm text-muted-foreground">{t('processChains.detail.noSubChains')}</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {children.map((child) => (
        <button
          key={child._id}
          onClick={() =>
            navigate({
              to: '/$slug/processes/process-chains/$chainId',
              params: { slug, chainId: child._id }
            })
          }
          className="flex items-start gap-3 rounded-lg border bg-card p-4 text-left shadow-xs transition-colors hover:bg-accent/50"
        >
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm truncate">{child.name}</h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              {child.childCount > 0 && (
                <span>{t('processChains.drillDown.childChains', { count: child.childCount })}</span>
              )}
              {child.processCount > 0 && (
                <span>{t('processChains.drillDown.processes', { count: child.processCount })}</span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export function ProcessChainDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useAuthStore().slug();
  const { chainId } = useParams({ from: '/$slug/processes/process-chains/$chainId' });
  const { data: chain, isLoading, error } = useProcessChain(chainId);
  const { can } = usePermissions();
  const canEdit = can('ProcessChain', 'update');
  const [editOpen, setEditOpen] = useState(false);
  const [editChainOpen, setEditChainOpen] = useState(false);

  useDocumentTitle(chain?.name || t('processChains.title'));

  // Build ancestor breadcrumb chain (simplified: just parent if exists)
  // For deep nesting, we'd need a dedicated ancestors API
  const parentId = chain?.parentChainId;
  const { data: parent } = useProcessChain(parentId || '');
  const ancestors = useMemo(() => {
    if (!parent) return [];
    return [{ _id: parent._id, name: parent.name }];
  }, [parent]);

  const handleProcessClick = (processId: string) => {
    navigate({
      to: '/$slug/processes/all-processes/$processId',
      params: { slug, processId },
      state: processDetailState('process-chains')
    });
  };

  if (isLoading) {
    return (
      <Page>
        <ProcessTabsNav />
        <PageContent>
          <Skeleton className="h-6 w-64 mb-4" />
          <Skeleton className="h-[400px] rounded-lg" />
        </PageContent>
      </Page>
    );
  }

  if (error || !chain) {
    return (
      <Page>
        <ProcessTabsNav />
        <PageContent>
          <p className="text-destructive">{t('common.error')}</p>
        </PageContent>
      </Page>
    );
  }

  const hasProcesses = chain.populatedProcesses.length > 0;
  const hasChildren = chain.childCount > 0;

  return (
    <Page>
      <ProcessTabsNav />
      <PageContent className="flex flex-col gap-6">
        {/* Breadcrumb */}
        <ChainBreadcrumb chain={chain} ancestors={ancestors} />

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold">{chain.name}</h1>
            {chain.description && (
              <p className="text-sm text-muted-foreground mt-1">{chain.description}</p>
            )}
          </div>
          {canEdit && (
            <div className="flex gap-2 shrink-0">
              <Button
                size="small"
                variant="ghost"
                onClick={() => setEditChainOpen(true)}
                title={t('processChains.editChain.title')}
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button size="small" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" />
                {t('processChains.edit.button')}
              </Button>
            </div>
          )}
        </div>

        {/* Content: Swimlane view OR Sub-chains list */}
        {hasProcesses && (
          <SwimlaneView processes={chain.populatedProcesses} onProcessClick={handleProcessClick} />
        )}
        {!hasProcesses && hasChildren && <SubChainsView parentChainId={chain._id} />}
        {!hasProcesses && !hasChildren && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Link2 className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              {t('processChains.detail.emptyChainHint')}
            </p>
            {canEdit && (
              <Button onClick={() => setEditOpen(true)}>
                <Plus className="h-4 w-4" />
                {t('processChains.edit.addProcesses')}
              </Button>
            )}
          </div>
        )}

        <EditProcessesDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          chainId={chain._id}
          chainName={chain.name}
          currentMappings={chain.populatedProcesses}
        />

        <EditChainDialog open={editChainOpen} onOpenChange={setEditChainOpen} chain={chain} />
      </PageContent>
    </Page>
  );
}
